import { PaymentMethod } from "./billing";
import { UserType } from "./mdi";

export const PASSWORD_RESET_TOKEN_KEY = "password-reset-token";

export interface Address {
  id?: string;
  street: string;
  secondary?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  default: boolean;
  UserId?: string;
}

export type AddressUpdate = Omit<Partial<Address>, "id">;

export type GenderType = "Male" | "Female" | "Other";

export enum Gender {
  Other = 0,
  Male = 1,
  Female = 2,
}

export const isGender = (gender: any): gender is Gender => {
  return Object.values(Gender).includes(gender);
};

export interface Link {
  id: string;
  url: string;
  token: string;
  visitors: number;
  leads: number;
  conversions: number;
}

export interface Affiliate {
  id: string;
  state: string;
  stripe_customer_id: string;
  visitors: number;
  leads: number;
  conversions: number;
  links: Link[];
}

export interface User {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string;
  email: string;
  gender?: Gender;
  phone_number: string;
  phone_type: number;
  date_of_birth: Date;
  stateOfResidence: string;
  height: number;
  weight: number;
  active: boolean;
  gender_label: string;
  current_medications: string;
  medicalConditions: string;
  allergies: string;
  "patient-id": string | null;
  pregnancy: boolean;
  addresses: Address[];
  password: string;
  confirmPassword: string;
  driver_license_id: string;
  /**
   * Client-only marker: the case id the uploaded driver_license_id belongs to.
   * Parental Consent treats the ID as uploaded only when this matches the active
   * case, so a prior consultation's ID cannot leak into a new one (ZD-3226
   * isolation). Intentionally NOT in userUpdateSchema, so it is never sent to
   * PATCH /user/profile.
   */
  driver_license_case: string;
  pharmacy_id: string;
  paymentMethods: PaymentMethod[];
  defaultPaymentMethod: string;
  partnerTx?: string;
  isAdmin: boolean;
  affiliateProfile?: Affiliate;
  promocodes: Array<{
    active: boolean;
    code: string;
  }>;
  createdAt?: number; // expressed in milliseconds
  /**
   * Server-set only: flips true when a clinician requests photo ID for
   * identity verification. The backend resets driver_license_id to null in
   * the same operation every time this fires, so a truthy driver_license_id
   * read while this is true can never be a leftover guardian-consent value.
   */
  id_requested: boolean;
}

// driver_license_case is a client-only ownership marker (ZD-3226) and must never
// reach the server, so it is excluded here — a whole-object Partial<User> spread
// into a USER_UPDATE payload then fails to compile rather than silently PATCHing it.
// id_requested is server-set only (clinician-initiated) and excluded for the same reason.
export type UserUpdate = Omit<
  Partial<User>,
  "id" | "createdAt" | "password" | "confirmPassword" | "driver_license_case" | "id_requested"
>;

const isAddress = (obj: unknown): obj is Address => {
  if (obj === null || typeof obj !== "object") return false;

  // Cast obj to a Record to ease property access.
  const o = obj as Record<string, unknown>;

  // Check required properties:
  if (typeof o.street !== "string") return false;
  if (typeof o.city !== "string") return false;
  if (typeof o.state !== "string") return false;
  if (typeof o.postalCode !== "string") return false;
  if (typeof o.country !== "string") return false;
  if (typeof o.default !== "boolean") return false;

  // Check optional properties (only if they exist):
  if ("id" in o && o.id !== undefined && typeof o.id !== "string") return false;
  if ("secondary" in o && o.secondary !== undefined && typeof o.secondary !== "string")
    return false;
  if ("UserId" in o && o.UserId !== undefined && typeof o.UserId !== "string") return false;

  return true;
};

// Define a schema mapping for each property in UserUpdate.
// Each key corresponds to a property name, and each function returns true
// if the value is either undefined (optional) or of the expected type.
const userUpdateSchema: Record<string, (value: unknown) => boolean> = {
  first_name: (v) => v === undefined || typeof v === "string",
  last_name: (v) => v === undefined || typeof v === "string",
  email: (v) => v === undefined || typeof v === "string",
  date_of_birth: (v) => v === undefined || typeof v === "string",
  gender: (v) => v === undefined || isGender(v),
  phone_number: (v) => v === undefined || typeof v === "string",
  phone_type: (v) => v === undefined || typeof v === "string",
  shipping_address: (v) => v === undefined || isAddress(v),
  driver_license_id: (v) => v === undefined || typeof v === "string",
  current_medications: (v) => v === undefined || typeof v === "string",
  medicalConditions: (v) => v === undefined || typeof v === "string",
  allergies: (v) => v === undefined || typeof v === "string",
  height: (v) => v === undefined || typeof v === "number",
  weight: (v) => v === undefined || typeof v === "number",
};

export const isUserUpdate = (obj: unknown): obj is UserUpdate => {
  // Check that the value is a non-null object.
  if (obj === null || typeof obj !== "object") return false;
  const o = obj as Record<string, unknown>;

  // Ensure there are no extra keys in the object.
  for (const key of Object.keys(o)) {
    if (!(key in userUpdateSchema)) {
      return false;
    }
  }

  // Validate each property using the schema.
  for (const key in userUpdateSchema) {
    if (!userUpdateSchema[key](o[key])) {
      return false;
    }
  }

  return true;
};

export interface PasswordUpdate {
  oldPassword: string;
  newPassword: string;
}

export interface Notification {
  event_type: string;
  case_id?: string;
  message?: Message;
  // See "Inbound Event Handling" in docs/architecture/messaging-realtime.md.
  id_requested?: boolean;
  driver_license_id?: string;
}

type Message = {
  id: string;
  user_type: UserType;
  user: {
    first_name: string;
  };
  text: string;
  files: any;
  updated_at: Date;
};

// Mirrors the backend's RFC 4122 UUID check (runway-node-server/utils/is-valid-uuid.ts).
const UUID_PATTERN =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i;

export const isValidUUID = (value: string): boolean => UUID_PATTERN.test(value);

export type IdentityVerificationStatus =
  | "not_requested"
  | "awaiting_submission"
  | "awaiting_confirmation"
  | "confirmed";

// A confirmed driver_license_id is always a bare UUID (the backend validates
// this before ever sending it to MDI, ENG-564); an unconfirmed S3 key always
// contains a slash and is never a bare UUID.
export const getIdentityVerificationStatus = (user: User): IdentityVerificationStatus => {
  if (isValidUUID(user.driver_license_id)) return "confirmed";
  if (!user.id_requested) return "not_requested";
  return user.driver_license_id ? "awaiting_confirmation" : "awaiting_submission";
};
